import { Event } from 'core/types';
import { updateRecordCacheForOfflineMode } from 'core/utils/optimistic-utils';
import { calendarKeys } from 'core/utils/query-key-factory';
import { produce } from 'immer';
import { QueryClient } from '@tanstack/react-query';
import { EventsCache } from './use-get-events';

export const removeEventsFromCache = (
	queryClient: QueryClient,
	calendarId: string,
	eventIds: string[]
) => {
	// Remove from the normalized cache
	queryClient.setQueryData<EventsCache>(
		calendarKeys.all(calendarId),
		currentCache => {
			const cache = currentCache
				? currentCache
				: { byId: {}, allIds: [] };

			return produce(cache, draft => {
				eventIds.forEach(eventId => {
					delete draft.byId[eventId];
				});

				draft.allIds = draft.allIds.filter(
					id => !eventIds.includes(id)
				);
			});
		}
	);

	// Also remove from list query caches to prevent the useEffect merge
	// in useGetEvents from re-adding deleted events
	const eventIdSet = new Set(eventIds);
	queryClient.setQueriesData<Event[]>(
		{ queryKey: calendarKeys.lists(calendarId) },
		data => data?.filter(event => !eventIdSet.has(event.uuid))
	);
};

export const saveEventsToCache = async (
	queryClient: QueryClient,
	calendarId: string,
	events: Event[]
) => {
	queryClient.setQueryData<EventsCache>(
		calendarKeys.all(calendarId),
		currentCache => {
			const cache = currentCache
				? currentCache
				: { byId: {}, allIds: [] };

			return produce(cache, draft => {
				events.forEach(event => {
					draft.byId[event.uuid] = event;
				});
				draft.allIds.push(...events.map(event => event.uuid));
			});
		}
	);

	for (const event of events) {
		await updateRecordCacheForOfflineMode(
			queryClient,
			calendarKeys.detail(calendarId, event.uuid),
			event
		);
	}
};

export const updateSingleEventInCache = (
	queryClient: QueryClient,
	calendarId: string,
	event: Event
) => {
	queryClient.setQueryData<EventsCache>(
		calendarKeys.all(calendarId),
		currentCache => {
			const cache = currentCache
				? currentCache
				: { byId: {}, allIds: [] };

			return produce(cache, draft => {
				draft.byId[event.uuid] = event;
			});
		}
	);
};
